// Setup Sentry - important that it is the first thing to be imported
import './instrument';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { ApolloProvider } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { UrlString } from '@azure/msal-common';

import '@ics-portal/styles/index.css';
import '@ics-portal/guardian/index.css';
import 'material-symbols/index.css';
import './style.scss';
import publicClientApplication from '@auth/msal/client';
import router from './Router';
import apolloClient from './api/client';
import Authenticating from './Authenticating';

if (import.meta.env.DEV) {
    loadDevMessages();
    loadErrorMessages();
}

const container = document.getElementById('root');
const root = createRoot(container as HTMLDivElement);

/**
 * Checks if the hash in the current URL contains known MSAL properties
 * The known properties are available during the authenticating process
 */
function isAuthPopup(): boolean {
    const hash = window.location.hash;
    return UrlString.hashContainsKnownProperties(hash);
}

/**
 * Prevent rendering the entire application if MSAL is in the process of authenticating a user.
 * Rendering the application during MSAL's authentication process may introduce a race condition.
 * This condition can cause conflict while checking a user's logged-in status and any subsequent redirects.
 */
if (import.meta.env.DEV && import.meta.env.VITE_LOCAL_AUTH_BYPASS === 'true') {
    const ls = window.localStorage;
    const env = 'login.windows.net';
    const tid = '00000000-0000-0000-0000-000000000001';
    const uid = '11111111-1111-1111-1111-111111111111';
    const hid = `${uid}.${tid}`;
    const key = `${hid}-${env}-${tid}`.toLowerCase();
    ls.setItem(
        key,
        JSON.stringify({
            homeAccountId: hid,
            environment: env,
            realm: tid,
            localAccountId: uid,
            username: 'admin@siemens-energy.com',
            name: 'Local Dev Admin',
            authorityType: 'MSSTS',
            clientInfo: btoa(JSON.stringify({ uid, utid: tid })),
            tenantId: tid,
        }),
    );
    ls.setItem('msal.account.keys', JSON.stringify([key]));
    window.open = function open() {
        return null;
    };
}

// Initialize MSAL - important that it is done before calling any other MSAL apis/methods
void publicClientApplication.initialize().then(() => {
    if (isAuthPopup()) {
        root.render(<Authenticating />);
    } else {
        root.render(
            <StrictMode>
                <MsalProvider instance={publicClientApplication}>
                    <ApolloProvider client={apolloClient}>
                        <RouterProvider router={router} />
                    </ApolloProvider>
                </MsalProvider>
            </StrictMode>,
        );
    }
});
